import { z } from 'zod';

// Person client schema
export const personClientSchema = z.object({
  type: z.literal('PERSON'),
  name: z.string().min(1, 'First name is required'),
  lastname: z.string().min(1, 'Last name is required'),
  middlename: z.string().optional().or(z.literal('')),
  pinfl: z.string().optional().or(z.literal('')),
  birthday: z
    .coerce
    .date()
    .refine((date) => !isNaN(date.getTime()), {
      message: 'Invalid date',
    })
    .refine((date) => date <= new Date(), {
      message: 'Birth date cannot be in the future',
    })
    .optional(),
  email: z
    .union([
      z.literal(''),
      z.string().email('Invalid email'),
    ])
    .optional(),
  phone: z.string().min(1, 'Phone is required'),
  companyId: z.number().optional(),
  country: z.string().optional().or(z.literal('')),
  region: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  street: z.string().optional().or(z.literal('')),
  zip: z.string().optional().or(z.literal('')),
});

// Company client schema
export const companyClientSchema = z.object({
  type: z.literal('COMPANY'),
  name: z.string().min(1, 'Company name is required'),
  inn: z.string().min(1, 'INN is required'),
  oked: z.string().optional().or(z.literal('')).or(z.literal(null)),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().min(1, 'Phone is required'),
  website: z.string().url().optional().or(z.literal('')).or(z.literal(null)),
  country: z.string().optional().or(z.literal('')).or(z.literal(null)),
  region: z.string().optional().or(z.literal('')).or(z.literal(null)),
  city: z.string().optional().or(z.literal('')).or(z.literal(null)),
  street: z.string().optional().or(z.literal('')).or(z.literal(null)),
  zip: z.string().optional().or(z.literal('')).or(z.literal(null)),
  building: z.string().optional().or(z.literal('')).or(z.literal(null)),
  bankName: z.string().optional().or(z.literal('')).or(z.literal(null)),
  bankCode: z
    .string()
    .optional()
    .or(z.literal(''))
    .transform((v) => (v ?? '').trim())
    .refine((v) => v === '' || /^\d+$/.test(v), 'Digits only'),
  bankAccountNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .transform((v) => (v ?? '').trim())
    .refine((v) => v === '' || /^\d+$/.test(v), 'Digits only'),
});

// Contact person schema for company employees
export const contactPersonSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Name is required'),
  role: z.string().optional().or(z.literal('')),
  phone: z.string().min(1, 'Phone is required'),
  email: z.string().email().optional().or(z.literal('')),
});

// Company with contact persons (for create)
export const companyWithContactsSchema = companyClientSchema.extend({
  contactPersons: z.array(contactPersonSchema).optional(),
});

// Types inferred from schemas
export type PersonClientFormData = z.infer<typeof personClientSchema>;
export type CompanyClientFormData = z.infer<typeof companyClientSchema>;
export type CompanyWithContactsFormData = z.infer<typeof companyWithContactsSchema>;
export type ContactPersonFormData = z.infer<typeof contactPersonSchema>;
